import React, { useEffect, useState } from "react";
import { Line } from "react-chartjs-2";

const SpendingLineChart = ({ chartData, interval }) => {
  if (!chartData || !chartData.datasets || !Array.isArray(chartData.datasets)) {
    return <p>Chart data is not available.</p>;
  }

  return (
    <Line
      className="analytics-line-large"
      data={chartData}
      options={{
        plugins: {
          tooltip: {
            enabled: true,
            displayColors: true,
            backgroundColor: "#eff6ff",
            titleFont: { size: 13 },
            titleColor: "#6783b8",
            titleMarginBottom: 6,
            bodyColor: "#1f2937",
            bodyFont: { size: 13 },
            bodySpacing: 6,
            callbacks: {
              label: (context) =>
                `${context.formattedValue} `,
            },
          },
        },
        maintainAspectRatio: false,
        scales: {
          y: {
            display: true,
            ticks: {
              beginAtZero: true,
              color: "#9eaecf",
              font: { size: 11 },
              padding: 8,
            },
            grid: {
              color: "rgba(82, 100, 132, 0.2)",
              tickMarkLength: 0,
            },
          },
          x: {
            display: interval !== "day",
            ticks: {
              color: "#9eaecf",
              font: { size: 12 },
              padding: 6,
            },
            grid: {
              color: "transparent",
              tickMarkLength: 0,
              offsetGridLines: true,
            },
          },
        },
      }}
    />
  );
};

const SendingSpendingChart = ({ sentMessages, receivedMessages, interval, setInterval, replyRateData }) => {
  const [chartData, setChartData] = useState({});
  const [totalMessages, setTotalMessages] = useState(0);
  const [totalReceivedMessages, setTotalReceivedMessages] = useState(0);

  useEffect(() => {
    const allLabels = Array.from(
      new Set([
        ...sentMessages.map((m) => m.interval || m.month),
        ...receivedMessages.map((m) => m.interval || m.month),
      ])
    ).sort();

    const labelMap = {};
    allLabels.forEach((label) => {
      labelMap[label] = { sent: 0, received: 0, cost: 0 };
    });

    sentMessages.forEach((m) => {
      const key = m.interval || m.month;
      if (labelMap[key]) {
        labelMap[key].sent = parseFloat(m.messageCount);
        labelMap[key].cost = parseFloat(m.totalPrice || 0);
      }
    });

    receivedMessages.forEach((m) => {
      const key = m.interval || m.month;
      if (labelMap[key]) {
        labelMap[key].received = parseFloat(m.messageCount);
      }
    });

    const finalLabels = Object.keys(labelMap);
    const sentCounts = finalLabels.map((label) => labelMap[label].sent);
    const receivedCounts = finalLabels.map((label) => labelMap[label].received);

    const totalMsg = sentCounts.reduce((a, b) => a + b, 0);
    const totalReceivedMsg = receivedCounts.reduce((a, b) => a + b, 0); // Calculate total received messages

    setTotalMessages(totalMsg);
    setTotalReceivedMessages(totalReceivedMsg); // Update state for total received messages

    setChartData({
      labels: finalLabels,
      datasets: [
        {
          label: "Messages Sent",
          data: sentCounts,
          backgroundColor: "rgba(52, 152, 219, 0.2)",
          borderColor: "rgba(52, 152, 219, 1)",
          borderWidth: 3,
          pointBackgroundColor: "rgba(52, 152, 219, 1)",
          pointRadius: 6,
          pointHoverRadius: 8,
          pointBorderWidth: 3,
          tension: 0.5,
          fill: true,
        },
        {
          label: "Messages Received",
          data: receivedCounts,
          backgroundColor: "rgba(46, 204, 113, 0.2)",
          borderColor: "rgba(46, 204, 113, 1)",
          borderWidth: 3,
          pointBackgroundColor: "rgba(46, 204, 113, 1)",
          pointRadius: 6,
          pointHoverRadius: 8,
          pointBorderWidth: 3,
          tension: 0.5,
          fill: true,
        },
      ],
    });
  }, [sentMessages, receivedMessages]);

  return (
    <>
      <div className="card-title-group pb-3 g-2">
        <div className="card-title card-title-sm">
          <h6 className="title">Usage Overview</h6>
          <p>See how many messages were sent and received, plus reply rate.</p>
        </div>
        <div>
          <select
            className="form-select"
            value={interval}
            onChange={(e) => setInterval(e.target.value)}
          >
            <option value="day">Last 60 days</option>
            <option value="week">Weekly</option>
            <option value="month">Monthly</option>
          </select>
        </div>
      </div>

      {sentMessages.length > 0 || receivedMessages.length > 0 ? (
        <div className="analytic-ov">
          <div className="analytic-data-group analytic-ov-group g-3">
            <div className="analytic-data analytic-ov-data">
              <div className="title">Total Sent</div>
              <div className="amount">{totalMessages}</div>
            </div>
            <div className="analytic-data analytic-ov-data">
              <div className="title">Total Received</div>
              <div className="amount">{totalReceivedMessages}</div>
            </div>
            {replyRateData && (
              <div className="analytic-data analytic-ov-data">
                <div className="title">All Time Reply Rate</div>
                <div className="amount">{replyRateData.replyRate}</div>
              </div>
            )}
          </div>

          <div className="analytic-ov-ck">
            <SpendingLineChart chartData={chartData} interval={interval} />
          </div>

          {interval !== "day" && (
            <div className="chart-label-group ms-5">
              {chartData.labels?.map((label, idx) => (
                <div key={idx} className="chart-label">
                  {label}
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <p>
          {interval === "day"
            ? "No messages in the last 60 days"
            : "No messages found"}
        </p>
      )}
    </>
  );
};

export default SendingSpendingChart;
